import {keyForMeshMaterialData, SceneNodes} from "@cm/template-nodes"
import {ThreeObject} from "@template-editor/helpers/three-object"
import {ThreeSceneManagerService} from "@template-editor/services/three-scene-manager.service"
import {Observable, of, tap} from "rxjs"
import {Three as THREE} from "@cm/material-nodes/three"

export class ThreePreloadMaterial extends ThreeObject<SceneNodes.PreloadMaterial> {
    protected override renderObject = new THREE.Mesh<THREE.BufferGeometry, THREE.Material>(new THREE.PlaneGeometry(0, 0))
    wasDisposed = false

    constructor(threeSceneManagerService: ThreeSceneManagerService, onAsyncUpdate: () => void) {
        super(threeSceneManagerService, onAsyncUpdate)

        const existingUvs = this.renderObject.geometry.getAttribute("uv")
        if (!existingUvs || existingUvs.count === 0) throw new Error("Mesh uv attribute is missing or empty")

        this.renderObject.geometry.setAttribute("uv1", existingUvs.clone())
        this.renderObject.geometry.setAttribute("uv2", existingUvs.clone())

        this.renderObject.frustumCulled = false
    }

    override setup(sceneNode: SceneNodes.PreloadMaterial) {
        const {sceneManagerService, materialManagerService} = this.threeSceneManagerService

        const setupDeferredMaterialMaterial = (
            mesh: THREE.Mesh<THREE.BufferGeometry, THREE.Material>,
            deferredMaterial: [Observable<THREE.Material>, string],
        ) => {
            const [material, key] = deferredMaterial
            sceneManagerService.addTask({
                description: `preloadMaterial(${key})`,
                task: material.pipe(
                    tap((material) => {
                        if (this.wasDisposed) {
                            materialManagerService.releaseMaterial(material)
                            return
                        }

                        materialManagerService.releaseMaterial(mesh.material)
                        mesh.material = material

                        this.onAsyncUpdate()
                    }),
                ),
                critical: true,
            })
        }

        const getMaterial = (): [THREE.Material, [Observable<THREE.Material>, string] | null] => {
            const {materialData, meshRenderSettings} = sceneNode
            const [defaultMaterial, deferredMaterial] = materialManagerService.acquireMaterial(materialData, meshRenderSettings, 0)
            if (!deferredMaterial) return [defaultMaterial, null]
            return [defaultMaterial, [deferredMaterial, keyForMeshMaterialData(materialData, meshRenderSettings)]]
        }

        const notifyCachedMaterial = () => {
            const {materialData, meshRenderSettings} = sceneNode
            sceneManagerService.addTask({
                description: `preloadCachedMaterial(${keyForMeshMaterialData(materialData, meshRenderSettings)})`,
                task: of(null),
                critical: true,
            })
        }

        const key = (preloadMaterial: SceneNodes.PreloadMaterial | undefined) => {
            if (preloadMaterial === undefined) return undefined
            return keyForMeshMaterialData(preloadMaterial.materialData, preloadMaterial.meshRenderSettings)
        }

        return (() => {
            if (key(sceneNode) !== key(this.parameters)) {
                const [material, deferredMaterial] = getMaterial()
                if (deferredMaterial) {
                    this.renderObject.material = material
                    setupDeferredMaterialMaterial(this.renderObject, deferredMaterial)
                    return false
                } else {
                    materialManagerService.releaseMaterial(this.renderObject.material)
                    this.renderObject.material = material
                    notifyCachedMaterial()
                    return true
                }
            } else return false
        })()
    }

    override dispose(final: boolean) {
        const {materialManagerService} = this.threeSceneManagerService

        this.renderObject.geometry.dispose()
        materialManagerService.releaseMaterial(this.renderObject.material)

        if (final) this.wasDisposed = true
    }
}
